import { ReactElement, ChangeEvent, useState } from 'react';
import { useForm } from 'react-hook-form';
import { PrimaryButton, Typography, Upload } from '@weareopenr/component-library';
import classnames from 'classnames/bind';
import styles from './FileUploadForm.module.scss';

declare global {
  interface Window {
    Cypress: Cypress.Cypress;
  }
}

const cx = classnames.bind(styles);

interface IFileUploadFormProps {
  formTitle: string;
  formMessage: string;
  isLoading?: boolean;
  fileExtension: string;
  onFormSubmit: ({ files }: { files: File }) => void;
}

const FileUploadForm = ({
  formTitle,
  formMessage,
  isLoading = false,
  fileExtension,
  onFormSubmit,
}: IFileUploadFormProps): ReactElement => {
  const [file, setFile] = useState<File | null>(null);
  const {
    handleSubmit,
    setValue,
    formState: { errors },
    register,
  } = useForm({
    defaultValues: {
      files: file,
    },
  });

  const handleFileUpload = ({ target }: ChangeEvent<HTMLInputElement>) => {
    setFile(target.files[0]);
    setValue('files', target.files[0]);
  };

  return (
    <form name="upload-form" className={styles['upload-form']} onSubmit={handleSubmit(onFormSubmit)}>
      <Typography size="xxlarge" weight="semi-bold">
        {formTitle}
      </Typography>
      <input
        id={`upload-${fileExtension.slice(1)}-file`}
        data-testid="upload-input"
        className={cx({
          'upload-input': !window.Cypress,
        })}
        type="file"
        {...register('files', {
          validate: (value) => value !== null || 'This field is mandatory',
          onChange: (e) => handleFileUpload(e),
        })}
        accept={fileExtension}
      />
      <div className={styles['upload-container']}>
        <label htmlFor={`upload-${fileExtension.slice(1)}-file`}>
          <div
            className={cx('upload-box', {
              'is-error': errors?.files?.message,
            })}
          >
            <Upload size="large" />
            <Typography size="medium" weight="regular" className={styles['form-message']}>
              {formMessage}
            </Typography>
            {file && (
              <Typography size="medium" weight="regular">
                {file?.name}
              </Typography>
            )}
          </div>
        </label>
        {errors?.files && (
          <Typography weight="regular" size="small" className={styles['error-message']}>
            {errors?.files?.message}
          </Typography>
        )}
      </div>
      <PrimaryButton size="full-width" loading={isLoading} type="submit" data-cy="import-button">
        {isLoading ? 'Importing…' : 'Import'}
      </PrimaryButton>
    </form>
  );
};

export default FileUploadForm;
